import { useEffect, useId, useRef, useState, type JSX, type KeyboardEvent } from 'react';

import { Chip } from './Chip.js';
import { cx } from './internal/cx.js';

export interface MultiSelectOption {
  value: string;
  label: string;
  /** Optional secondary line shown under the label in the list. */
  description?: string;
}

export interface MultiSelectProps {
  value: readonly string[];
  onChange: (next: string[]) => void;
  options: readonly MultiSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  /** Accessible name when no visible label. */
  'aria-label'?: string;
  /** Id(s) of helper text describing the field. */
  'aria-describedby'?: string;
}

/**
 * Multi-select dropdown with removable chips for selected values.
 *
 * @remarks
 * Models the same open/close and keyboard patterns as Autocomplete, but keeps
 * the list open while toggling options so users can select several items in
 * one pass.
 *
 * @example
 * ```tsx
 * <MultiSelect
 *   aria-label="Programs"
 *   value={programs}
 *   onChange={setPrograms}
 *   options={[{ value: 'hola', label: 'HOLA' }]}
 * />
 * ```
 */
export function MultiSelect({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  disabled,
  id,
  className,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
}: MultiSelectProps): JSX.Element {
  const listboxId = useId();
  const generatedId = useId();
  const triggerId = id ?? generatedId;
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);

  const selectedOptions = options.filter((opt) => value.includes(opt.value));
  const activeOptionId =
    open && options.length > 0 && highlightIndex >= 0
      ? `${listboxId}-option-${highlightIndex}`
      : undefined;

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current !== undefined) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: PointerEvent): void {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [open]);

  function toggleOption(opt: MultiSelectOption): void {
    if (value.includes(opt.value)) {
      onChange(value.filter((v) => v !== opt.value));
      return;
    }
    onChange([...value, opt.value]);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (disabled) return;

    if (!open && (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      setOpen(true);
      setHighlightIndex(0);
      return;
    }
    if (!open) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, options.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const opt = options[highlightIndex];
      if (opt) toggleOption(opt);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
    }
  }

  return (
    <div ref={rootRef} className={cx('nrx-multi-select', className)}>
      <div
        ref={triggerRef}
        id={triggerId}
        role="combobox"
        tabIndex={disabled ? -1 : 0}
        aria-expanded={open}
        aria-controls={open && options.length > 0 ? listboxId : undefined}
        aria-haspopup="listbox"
        aria-activedescendant={activeOptionId}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-disabled={disabled || undefined}
        className={cx(
          'input',
          'nrx-multi-select__trigger',
          disabled && 'nrx-multi-select__trigger--disabled',
        )}
        onClick={() => {
          if (disabled) return;
          setOpen((prev) => !prev);
          setHighlightIndex(0);
        }}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          if (blurTimeoutRef.current !== undefined) {
            clearTimeout(blurTimeoutRef.current);
          }
          blurTimeoutRef.current = setTimeout(() => setOpen(false), 150);
        }}
      >
        {selectedOptions.length === 0 ? (
          <span className="nrx-multi-select__placeholder">{placeholder}</span>
        ) : (
          <span className="nrx-multi-select__chips">
            {selectedOptions.map((opt) => (
              <Chip
                key={opt.value}
                tone="info"
                variant="filled"
                deleteLabel={`Remove ${opt.label}`}
                onDelete={() => {
                  onChange(value.filter((v) => v !== opt.value));
                }}
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
              >
                {opt.label}
              </Chip>
            ))}
          </span>
        )}
        <span className="nrx-multi-select__chevron" aria-hidden="true" />
      </div>
      {open && options.length > 0 ? (
        <ul
          id={listboxId}
          role="listbox"
          aria-multiselectable="true"
          className="nrx-multi-select__list"
        >
          {options.map((opt, index) => {
            const selected = value.includes(opt.value);
            return (
              <li
                key={opt.value}
                id={`${listboxId}-option-${index}`}
                role="option"
                aria-selected={selected}
                className={cx(
                  'nrx-multi-select__option',
                  index === highlightIndex && 'nrx-multi-select__option--highlighted',
                  selected && 'nrx-multi-select__option--selected',
                )}
                onMouseDown={(event) => {
                  event.preventDefault();
                  toggleOption(opt);
                }}
                onMouseEnter={() => setHighlightIndex(index)}
              >
                <span className="nrx-multi-select__option-check" aria-hidden="true">
                  {selected ? '✓' : ''}
                </span>
                <span className="nrx-multi-select__option-body">
                  <span className="nrx-multi-select__option-label">{opt.label}</span>
                  {opt.description ? (
                    <span className="nrx-multi-select__option-description">{opt.description}</span>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
